import { Text, UnstyledButton } from "@mantine/core";
import clsx from "clsx";

import SessionPopover from "../SessionPopover";
import { timeOf } from "../utils/sessionTime";
import { minutesToOffset } from "./calendarLayout";
import { STICKY_MIN_HEIGHT } from "./constants";
import classes from "./SessionsCalendar.module.css";

// Heights below which the block drops its speakers, then its time label
const COMPACT_HEIGHT = 72;
const TINY_HEIGHT = 44;
// Leaves a hairline between stacked blocks
const BLOCK_GAP = 2;

export default function SessionBlock({
  block,
  column,
  day,
  startHour,
  openedSessionId,
  enableActions,
  tagColors,
  onToggle,
  onClose,
  onDelete,
}) {
  const { session, startMin, endMin, lane, laneCount } = block;
  const isCanceled = session.canceledAt !== null;

  const top = minutesToOffset(startMin - startHour * 60);
  const height = minutesToOffset(endMin - startMin) - BLOCK_GAP;
  const width = `calc(${100 / laneCount}% - 6px)`;
  const left = `calc(${(lane * 100) / laneCount}% + 2px)`;

  const timeLabel = `${timeOf(session.starting)} – ${timeOf(session.ending)}`;
  const speakerNames = (session.speakers ?? [])
    .map((speaker) => `${speaker?.firstName} ${speaker?.lastName}`)
    .join(", ");

  const isCompact = height < COMPACT_HEIGHT;
  const isTiny = height < TINY_HEIGHT;
  const isTall = height >= STICKY_MIN_HEIGHT;

  return (
    <SessionPopover
      session={session}
      day={day}
      opened={openedSessionId === session.id}
      onClose={onClose}
      onDelete={() => onDelete(session.id)}
      enableActions={enableActions}
      tagColors={tagColors}
    >
      <UnstyledButton
        className={clsx(classes.session, {
          [classes.sessionCanceled]: isCanceled,
        })}
        style={{
          top,
          height,
          left,
          width,
          "--session-accent": `var(--mantine-color-${column.color}-8)`,
          "--session-bg": `var(--mantine-color-${column.color}-1)`,
          "--session-time": `var(--mantine-color-${column.color}-9)`,
        }}
        onClick={() => onToggle(session.id)}
        aria-label={[
          session.title,
          isCanceled && "canceled",
          timeLabel,
          column.name,
        ]
          .filter(Boolean)
          .join(", ")}
      >
        {/* A long session pins its text below the header so it stays in view */}
        <div className={isTall ? classes.stickyContent : undefined}>
          {!isTiny && <div className={classes.sessionTime}>{timeLabel}</div>}
          <Text
            className={classes.sessionTitle}
            lineClamp={isCompact ? 1 : 2}
            component="div"
          >
            {session.title}
          </Text>
          {!isCompact && speakerNames && (
            <div className={classes.sessionSpeakers}>{speakerNames}</div>
          )}
        </div>
      </UnstyledButton>
    </SessionPopover>
  );
}
